/**
 * SettingsPopover Component
 * Additional settings menu for toggling visibility options
 */

import React, { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import type { SettingsPopoverProps } from './types';

export const SettingsPopover: React.FC<SettingsPopoverProps> = ({
  showTrustScore,
  showTags,
  showActions,
  compactMode,
  availableSettings,
  onToggleTrustScore,
  onToggleTags,
  onToggleActions,
  onToggleCompact,
  onReset,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  if (availableSettings.length === 0) {
    return null; // No settings available
  }

  return (
    <div className="ai360-settings-popover" ref={popoverRef}>
      <button
        type="button"
        className={clsx(
          'ai360-settings-popover-trigger',
          isOpen && 'ai360-settings-popover-trigger--active'
        )}
        onClick={() => setIsOpen(!isOpen)}
        title="Opciones de visualización"
        aria-label="Opciones de visualización"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M10 5.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM10 11.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM10 17.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"
            fill="currentColor"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="ai360-settings-popover-menu">
          <div className="ai360-settings-popover-header">
            <h4>Opciones de visualización</h4>
          </div>

          <div className="ai360-settings-popover-content">
            {availableSettings.includes('trustscore') && (
              <label className="ai360-settings-popover-item">
                <input
                  type="checkbox"
                  checked={showTrustScore}
                  onChange={onToggleTrustScore}
                />
                <span>Mostrar TrustScore</span>
              </label>
            )}

            {availableSettings.includes('tags') && (
              <label className="ai360-settings-popover-item">
                <input
                  type="checkbox"
                  checked={showTags}
                  onChange={onToggleTags}
                />
                <span>Mostrar etiquetas</span>
              </label>
            )}

            {availableSettings.includes('actions') && (
              <label className="ai360-settings-popover-item">
                <input
                  type="checkbox"
                  checked={showActions}
                  onChange={onToggleActions}
                />
                <span>Mostrar botones</span>
              </label>
            )}

            {availableSettings.includes('compact') && (
              <label className="ai360-settings-popover-item">
                <input
                  type="checkbox"
                  checked={compactMode}
                  onChange={onToggleCompact}
                />
                <span>Modo compacto</span>
              </label>
            )}
          </div>

          <div className="ai360-settings-popover-footer">
            <button
              type="button"
              className="ai360-settings-popover-reset"
              onClick={() => {
                onReset();
                setIsOpen(false);
              }}
            >
              Restablecer valores
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
